import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { 
  X, Download, Share2, Check, Search, MessageCircle, Send,
  Mail, Loader2, FileText, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRICE_COLUMNS = [
  { key: 'ptr', label: 'PTR (₹)' },
  { key: 'pts', label: 'PTS (₹)' },
  { key: 'mrp', label: 'MRP (₹)' },
  { key: 'ptr_usd', label: 'PTR (USD)' },
  { key: 'pte_usd', label: 'PTE (USD)' },
];

const ShareCatalogDialog = ({ open, onClose, user, products, defaultSelected = [] }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set(defaultSelected));
  const [priceColumn, setPriceColumn] = useState('ptr');
  const [title, setTitle] = useState('Product Catalog');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // { blob, url, filename }
  const [includeImage, setIncludeImage] = useState(true);

  // Reset all state when dialog closes; revoke blob URL to avoid leaks
  useEffect(() => {
    if (!open) {
      if (generated?.url) {
        try { window.URL.revokeObjectURL(generated.url); } catch (_) {}
      }
      setSearch('');
      setSelected(new Set(defaultSelected));
      setPriceColumn('ptr');
      setTitle('Product Catalog');
      setIncludeImage(true);
      setGenerated(null);
      setGenerating(false);
    }
    // eslint-disable-next-line
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return products;
    const q = search.toLowerCase();
    return products.filter(p =>
      p.product_name?.toLowerCase().includes(q) ||
      p.generic_name?.toLowerCase().includes(q) ||
      p.manufacturer?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const toggleProduct = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllFiltered = () => {
    const next = new Set(selected);
    filtered.forEach(p => next.add(p.product_name));
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const handleGenerate = async () => {
    if (selected.size === 0) {
      toast.warning('Pick at least one product to share');
      return;
    }
    setGenerating(true);
    // Revoke previous URL if regenerating
    if (generated?.url) {
      try { window.URL.revokeObjectURL(generated.url); } catch (_) {}
    }
    setGenerated(null);
    try {
      const res = await axios.post(
        `${API}/price-list/share-catalog`,
        {
          product_names: Array.from(selected),
          price_column: priceColumn,
          title: title || 'Product Catalog',
          include_image: includeImage
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = `catalog_${new Date().toISOString().slice(0,10)}.pdf`;
      setGenerated({ blob, url, filename });
      toast.success(`Catalog ready — ${selected.size} products`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate catalog');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generated) return;
    const a = document.createElement('a');
    a.href = generated.url;
    a.download = generated.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleNativeShare = async () => {
    if (!generated) return;
    try {
      const file = new File([generated.blob], generated.filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: title || 'Product Catalog',
          text: `${title} — ${selected.size} products`,
          files: [file]
        });
      } else {
        // Fallback: download
        handleDownload();
        toast.info('Native share not supported — file downloaded instead');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error('Share failed');
      }
    }
  };

  const handlePlatformShare = (platform) => {
    if (!generated) {
      handleDownload();
      toast.info('Download starting — share the downloaded PDF on your platform');
      return;
    }
    // Most messaging platforms require an attachment via native share.
    // For desktop, we download the file then open the platform's web compose with a prefilled message.
    handleDownload();
    const text = encodeURIComponent(`${title} — ${selected.size} products. Attaching the PDF catalog.`);
    const appUrl = encodeURIComponent(window.location.origin);
    let url = '';
    if (platform === 'whatsapp') url = `https://wa.me/?text=${text}`;
    else if (platform === 'telegram') url = `https://t.me/share/url?url=${appUrl}&text=${text}`;
    else if (platform === 'email') url = `mailto:?subject=${encodeURIComponent(title)}&body=${text}`;
    if (url) window.open(url, '_blank');
    toast.info('Attach the downloaded PDF to your message');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[65] flex items-center justify-center p-3 sm:p-6" data-testid="share-catalog-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Catalog
            </h3>
            <p className="text-xs text-indigo-100 mt-0.5">
              Select products → generate PDF → download or share to any platform
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="share-catalog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Left: product selection */}
          <div className="flex-1 flex flex-col border-r border-slate-200 min-w-0">
            <div className="p-3 border-b border-slate-200 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="share-catalog-search"
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">
                  <strong>{selected.size}</strong> selected • {filtered.length} shown
                </span>
                <div className="flex gap-1">
                  <button onClick={selectAllFiltered} className="px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded">
                    Select all
                  </button>
                  <button onClick={clearSelection} className="px-2 py-1 text-slate-500 hover:bg-slate-100 rounded">
                    Clear
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filtered.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No products match</div>
              ) : (
                <div className="space-y-1">
                  {filtered.slice(0, 200).map(p => {
                    const isSel = selected.has(p.product_name);
                    return (
                      <label
                        key={p.product_name}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${
                          isSel ? 'border-indigo-400 bg-indigo-50/50' : 'border-transparent hover:bg-slate-50'
                        }`}
                        data-testid={`share-product-row-${p.product_name}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleProduct(p.product_name)}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        {p.product_image ? (
                          <img src={p.product_image} alt="" className="w-8 h-8 rounded object-cover border" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                            <FileText className="w-3.5 h-3.5 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.product_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {p.packing} {p.manufacturer ? ` • ${p.manufacturer}` : ''}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                  {filtered.length > 200 && (
                    <p className="text-center text-xs text-slate-400 py-2">
                      Showing first 200 — refine search to see more
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: options + share actions */}
          <div className="w-full md:w-72 flex flex-col bg-slate-50">
            <div className="p-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Catalog title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="share-catalog-title"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Price column</label>
                <div className="relative">
                  <select
                    value={priceColumn}
                    onChange={(e) => setPriceColumn(e.target.value)}
                    className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    data-testid="share-catalog-price-col"
                  >
                    {PRICE_COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeImage}
                  onChange={(e) => setIncludeImage(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-sm">Include product images</span>
              </label>

              <button
                onClick={handleGenerate}
                disabled={generating || selected.size === 0}
                data-testid="share-catalog-generate"
                className="w-full mt-2 px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {generating ? 'Generating PDF...' : `Generate (${selected.size})`}
              </button>

              {generated && (
                <div className="border-t border-slate-200 pt-3 space-y-2">
                  <p className="text-xs font-medium text-slate-700">PDF Ready — share via:</p>
                  <button
                    onClick={handleNativeShare}
                    data-testid="share-native"
                    className="w-full px-3 py-2 bg-slate-800 text-white rounded-md text-sm font-medium hover:bg-slate-900 flex items-center justify-center gap-1.5"
                  >
                    <Share2 className="w-4 h-4" /> Native Share
                  </button>
                  <button
                    onClick={handleDownload}
                    data-testid="share-download"
                    className="w-full px-3 py-2 bg-white border border-slate-300 text-slate-800 rounded-md text-sm font-medium hover:bg-slate-50 flex items-center justify-center gap-1.5"
                  >
                    <Download className="w-4 h-4" /> Download PDF
                  </button>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      onClick={() => handlePlatformShare('whatsapp')}
                      title="WhatsApp"
                      data-testid="share-whatsapp"
                      className="px-2 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md flex items-center justify-center"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePlatformShare('telegram')}
                      title="Telegram"
                      data-testid="share-telegram"
                      className="px-2 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-md flex items-center justify-center"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePlatformShare('email')}
                      title="Email"
                      data-testid="share-email"
                      className="px-2 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-md flex items-center justify-center"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-tight pt-1">
                    Tip: On mobile, use Native Share to attach the PDF directly in any app. On desktop, the PDF downloads — then attach it in your messaging app.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCatalogDialog;
